import React, { useEffect, useRef, useState } from 'react';
import { Check, Loader2, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { formatRut, validateRut } from '../utils/rutValidation';

const cache = new Map();

// Shared lookup (Boostr via backend, cached here and server-side) so every form reuses the same result
export const lookupRut = async (api, rut) => {
  const key = rut.replace(/[^0-9kK]/g, '').toUpperCase();
  if (!cache.has(key)) cache.set(key, api.get('/crm/rut-lookup', { params: { rut } }).then((r) => {
    if (!r.data.success) cache.delete(key);
    return r.data;
  }).catch(() => { cache.delete(key); return { success: false }; }));
  return cache.get(key);
};

// Fills only empty targets: { field: value } -> object with the empty ones set
export const fillEmpty = (obj, values) => ({ ...obj, ...Object.fromEntries(Object.entries(values).filter(([k, v]) => k && v && !String(obj?.[k] ?? '').trim())) });

// For generic forms: the name / business-activity fields that belong to the same party as a RUT field (empleador_rut -> empleador_nombre, giro_empleador)
export const rutTargets = (rutKey, keys) => {
  const role = rutKey.toLowerCase().split(/[_\s-]+/).filter((t) => t !== 'rut');
  const same = (k) => !role.length || role.some((r) => k.toLowerCase().includes(r));
  const pick = (re) => keys.find((k) => k !== rutKey && re.test(k.toLowerCase()) && same(k)) || (role.length ? null : keys.find((k) => re.test(k.toLowerCase())));
  return { name: pick(/nombre|razon|name/), giro: pick(/giro|actividad/) };
};

export const lookupValues = (rutKey, keys, d) => {
  const t = rutTargets(rutKey, keys);
  return Object.fromEntries([[t.name, d.name], [t.giro, (d.activities || []).join(', ')]].filter(([k]) => k));
};

// "JUAN ANDRÉS PÉREZ SOTO" -> nombres + apellidos (last two words are the surnames)
export const splitName = (full) => {
  const t = (full || '').trim().split(/\s+/).filter(Boolean);
  return t.length < 3 ? { nombre: full, paterno: '', materno: '' } : { nombre: t.slice(0, -2).join(' '), paterno: t[t.length - 2], materno: t[t.length - 1] };
};

// Drop-in RUT input: formats as 12.345.678-5 while typing, validates the check digit and, once valid, looks it up and calls onLookup({ name, activities })
export const RutField = ({ value, onChange, onLookup, className = '', testid, placeholder = '12.345.678-5', clean = false, ...rest }) => {
  const { api } = useAuth();
  const [state, setState] = useState(null);
  const v = value ? formatRut(value) : '';
  const digits = v.replace(/[^0-9kK]/g, '');
  const [edited, setEdited] = useState(null);
  const callback = useRef(onLookup);
  callback.current = onLookup;
  const valid = digits.length >= 8 && validateRut(v).isValid;

  useEffect(() => {
    setState(null);
    if (!valid || !callback.current || edited !== digits) return undefined;
    let live = true;
    const t = setTimeout(async () => {
      setState('loading');
      const d = await lookupRut(api, v);
      if (!live) return;
      setState(d.success ? 'found' : 'notfound');
      if (d.success) callback.current?.(d);
    }, 500);
    return () => { live = false; clearTimeout(t); };
  }, [digits, valid, edited, api, v]);

  const change = (e) => {
    const f = formatRut(e.target.value.replace(/[^0-9kK.-]/g, ''));
    setEdited(f.replace(/[^0-9kK]/g, ''));
    onChange(clean ? f.replace(/\./g, '') : f);
  };
  return (
    <div className="relative w-full">
      <input {...rest} data-testid={testid} value={v} onChange={change} placeholder={placeholder} maxLength={12} autoComplete="off" className={`${className} pr-8`} />
      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2">
        {state === 'loading' ? <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" /> : digits.length >= 8 && (valid ? <Check data-testid={testid && `${testid}-valid`} className="h-3.5 w-3.5 text-emerald-400" /> : <X data-testid={testid && `${testid}-invalid`} className="h-3.5 w-3.5 text-red-400" />)}
      </span>
      {digits.length >= 8 && !valid && <p data-testid={testid && `${testid}-error`} className="mt-0.5 text-[10px] text-red-400">RUT inválido: revisa el dígito verificador</p>}
      {state === 'found' && <p data-testid={testid && `${testid}-found`} className="mt-0.5 text-[10px] text-emerald-400">Datos encontrados en el rutificador</p>}
      {state === 'notfound' && <p data-testid={testid && `${testid}-notfound`} className="mt-0.5 text-[10px] text-slate-500">Consulta no disponible o sin datos; puedes completar los campos manualmente</p>}
    </div>
  );
};
